import { MEDIA_SERVICE_EEC_BASE } from './eecLabelUrl';

/**
 * Hosts permitted to serve an EEC energy label image.
 *
 * The label is a legally mandated disclosure, so the URL is pinned rather than trusted. It reaches
 * us from the VTP API and is then persisted on the session record — and session records accept
 * unauthenticated writes, so anyone holding a session id could otherwise point the label at an
 * arbitrary host. That would allow both content spoofing of the efficiency rating and an
 * attacker-chosen outbound request from every viewer's browser, including inside third-party
 * dealer portals where the app runs as an embedded web component.
 */
const TRUSTED_LABEL_HOSTS: ReadonlySet<string> = new Set(['mediaservice.audi.com']);

// Derived from the URL builder's base so the pin and the builder can never drift on the path.
// The trailing slash requires an actual label path below the prefix, not the bare directory.
const TRUSTED_LABEL_PATH_PREFIX = `${new URL(MEDIA_SERVICE_EEC_BASE).pathname}/`;

/**
 * True when `url` is safe to use directly as an energy label image source.
 *
 * Requires https on a known Audi media host, the default port, no credentials, no query or
 * fragment, and a path under the EEC label prefix — a trusted host alone is not enough, because
 * the URL is attacker-influenceable and the image fetch must not be repurposable against other
 * MediaService endpoints. URL parsing resolves dot segments (including percent-encoded ones)
 * before the prefix check, so traversal cannot escape it. Anything else — a foreign host, a
 * scheme downgrade, `data:`/`javascript:`, or an unparseable string — is rejected so the caller
 * falls back to building the URL from the efficiency class.
 */
export function isTrustedEnergyLabelUrl(url: string | undefined): boolean {
  if (!url) return false;

  try {
    const parsed = new URL(url);
    return (
      parsed.protocol === 'https:' &&
      TRUSTED_LABEL_HOSTS.has(parsed.hostname) &&
      parsed.port === '' &&
      parsed.username === '' &&
      parsed.password === '' &&
      parsed.search === '' &&
      parsed.hash === '' &&
      parsed.pathname.startsWith(TRUSTED_LABEL_PATH_PREFIX)
    );
  } catch {
    return false;
  }
}
